import type {
  SubscriptionOverviewResponse,
  SubscriptionPaymentMethod,
} from "@nlc/shared-types";

import { CreditCardIcon, PlusIcon } from "lucide-react";

import { Button } from "@/components/button";
import { cn } from "@/utils/cn";

import { CardBrandMark } from "./CardBrandMark";

// The subscription backend does not expose saved cards yet, so the snapshot
// falls back to the two cards shown in the Figma frame. Drop the fallback once
// the endpoint fills data.paymentMethods.
const MOCK_PAYMENT_METHODS: SubscriptionPaymentMethod[] = [
  {
    brand: "visa",
    expiryMonth: 4,
    expiryYear: 2027,
    isDefault: true,
    last4: "4242",
  },
  // {
  //   brand: "mastercard",
  //   expiryMonth: 11,
  //   expiryYear: 2026,
  //   isDefault: false,
  //   last4: "8812",
  // },
];

const MAX_VISIBLE_PAYMENT_METHODS = 2;

export function PaymentMethodsCard({
  data,
  onNavigate,
}: {
  data: Pick<SubscriptionOverviewResponse, "paymentMethods">;
  onNavigate: () => void;
}) {
  const paymentMethods = data.paymentMethods ?? MOCK_PAYMENT_METHODS;
  const canAddPaymentMethod =
    paymentMethods.length < MAX_VISIBLE_PAYMENT_METHODS;

  return (
    <div className="min-w-0 inline-flex flex-col justify-between gap-xs rounded-xl border border-border-secondary bg-bg-primary py-xl px-3xl">
      <div className="flex items-start justify-between">
        <h3 className="text-xl font-semibold text-text-primary">
          Payment methods
        </h3>
        <div className="h-10 w-10 rounded-md bg-bg-brand-secondary flex items-center justify-center">
          <CreditCardIcon className="text-text-brand-primary" size={20} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-lg gap-y-xs">
        {paymentMethods.length === 0 ? (
          <p className="flex-1 text-sm text-text-tertiary">
            No payment methods on file
          </p>
        ) : (
          paymentMethods
            .slice(0, MAX_VISIBLE_PAYMENT_METHODS)
            .map((paymentMethod) => (
              <span
                className={cn(
                  "flex min-w-0 items-center gap-md rounded-md p-lg",
                  paymentMethod.isDefault && "bg-bg-secondary",
                )}
                key={`${paymentMethod.brand}-${paymentMethod.last4}`}
              >
                <CardBrandMark brand={paymentMethod.brand} />
                <span className="text-sm font-medium text-text-primary">
                  •••• {paymentMethod.last4}
                </span>
              </span>
            ))
        )}

        {canAddPaymentMethod && (
          <Button
            className="px-xs"
            color="color"
            // redirect to payment methods tab and open the add new method modal
            onPress={onNavigate}
            size="lg"
            variant="tertiary"
          >
            <PlusIcon />
            Add new method
          </Button>
        )}
      </div>
    </div>
  );
}
